import { invoke } from '~/lib/tauriInvoke'
import * as Sentry from '@sentry/nuxt'
import { loadAppSettings, patchAppSettings, type UserPresenceStatus } from '~/lib/settings/appSettings'
import { useIdleDetection } from '~/composables/useIdleDetection'

export type { UserPresenceStatus }

const STATUS_CODES: Record<UserPresenceStatus, number> = {
    ONLINE: 0,
    DO_NOT_DISTURB: 2,
    INVISIBLE: 3,
}

const currentStatus = ref<UserPresenceStatus>('ONLINE')

// What others see of this device, for the own-avatar dot: «Онлайн» turns into «Нет на месте» while
// this device is idle (the server does the same once all of the account's devices are idle).
const { isIdle } = useIdleDetection()
const displayStatus = computed<UserPresenceStatus | 'IDLE'>(() =>
    currentStatus.value === 'ONLINE' && isIdle.value ? 'IDLE' : currentStatus.value)

// Every status change is fanned out by the server to all of the user's groups, so a burst of
// switches must reach it as one request carrying the final state, not one request per switch.
const STATUS_SEND_DEBOUNCE_MS = 2000

// Last status the server acknowledged — what the UI falls back to if a later update fails.
let confirmedStatus: UserPresenceStatus | null = null

interface PendingStatusSend {
    timer: ReturnType<typeof setTimeout> | null
    /** Shared by every `setStatus` call collapsed into this send. */
    promise: Promise<void>
    resolve: () => void
    reject: (error: unknown) => void
    /** Status shown before the first change of this burst. */
    previous: UserPresenceStatus
}
let pendingSend: PendingStatusSend | null = null
// Sends run strictly one after another, so an update that's still retrying can't land after
// (and overwrite) a newer one.
let sendChain: Promise<void> = Promise.resolve()

async function flushPendingStatus() {
    const pending = pendingSend
    if (!pending) return
    pendingSend = null

    // Read at send time, not at schedule time: only the final state of the burst goes out.
    const status = currentStatus.value
    if (status === confirmedStatus) {
        pending.resolve()
        return
    }

    try {
        await invoke('update_user_status', { status: STATUS_CODES[status] })
        confirmedStatus = status
        pending.resolve()
    } catch (e) {
        console.error('[UserStatus] Failed to update status:', e)
        Sentry.captureException(e, { extra: { action: 'set_status', status } })
        // Roll back only if the user hasn't picked something else in the meantime.
        if (!pendingSend && currentStatus.value === status) {
            const fallback = confirmedStatus ?? pending.previous
            currentStatus.value = fallback
            patchAppSettings({ presence: { status: fallback } })
        }
        pending.reject(e)
    }
}

const STATUS_BY_CODE: Record<number, UserPresenceStatus> = {
    0: 'ONLINE',
    2: 'DO_NOT_DISTURB',
    3: 'INVISIBLE',
}

/**
 * The server's personal `UserStatusChanged` carries the status this account picked
 * (`PreferredStatus`). When another device of the same account changed it, show that here too —
 * without sending anything back. A local change still waiting to be sent wins: it goes out after this.
 */
export function applyRemotePreferredStatus(code: number) {
    const status = STATUS_BY_CODE[code]
    if (!status || pendingSend) return
    confirmedStatus = status
    if (currentStatus.value === status) return
    currentStatus.value = status
    patchAppSettings({ presence: { status } })
}

/** Logout: back to the default until the next account's status is loaded. */
export function resetUserStatus() {
  currentStatus.value = 'ONLINE'
}

export function useUserStatus() {
    function loadStatus(): UserPresenceStatus {
        currentStatus.value = loadAppSettings().presence.status
        return currentStatus.value
    }

    async function applyStartupStatus() {
        const status = loadStatus()
        try {
            await invoke('update_user_status', { status: STATUS_CODES[status] })
            confirmedStatus = status
        } catch (e) {
            console.error('[UserStatus] Failed to apply startup status:', e)
            Sentry.captureException(e, { extra: { action: 'apply_startup_status', status } })
        }
    }

    // The UI (and the persisted setting) switches immediately; the request itself is debounced.
    // The returned promise settles once the server has the final state, and rejects — after the
    // UI has been rolled back — if that update fails.
    function setStatus(status: UserPresenceStatus): Promise<void> {
        const previous = currentStatus.value
        currentStatus.value = status
        patchAppSettings({ presence: { status } })

        if (!pendingSend) {
            let resolve!: () => void
            let reject!: (error: unknown) => void
            const promise = new Promise<void>((res, rej) => {
                resolve = res
                reject = rej
            })
            pendingSend = { timer: null, promise, resolve, reject, previous }
        }
        if (pendingSend.timer !== null) clearTimeout(pendingSend.timer)
        pendingSend.timer = setTimeout(() => {
            sendChain = sendChain.then(flushPendingStatus)
        }, STATUS_SEND_DEBOUNCE_MS)
        return pendingSend.promise
    }

    return {
        currentStatus,
        displayStatus,
        loadStatus,
        applyStartupStatus,
        setStatus,
    }
}
